import { useState } from "react";
import { getThemeClasses } from "../utils/ThemeClasses";
import Modal from "./base/Modal";
import api from "../utils/api";
import { REPORT_DEFAULT_RECIPIENTS } from "../features/dashboards/reportingConfig";

interface UnitItem {
  id: string;
  name: string;
  plate: string;
  companyId: string;
  companyName: string;
}

interface CriticalCompany {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  reportingUnits: number;
  totalUnits: number;
  percentage: number;
}

interface CompanyWithPriority extends CriticalCompany {
  priority: "healthy" | "warning" | "highRisk" | "critical";
  offlineUnits: number;
}

interface PlatformAlertsProps {
  companies: CompanyWithPriority[];
  totalClients: number;
  allUnits: UnitItem[];
  platformName: string;
  themeClasses: ReturnType<typeof getThemeClasses>;
  isDarkMode: boolean;
}

export default function PlatformAlerts({
  companies,
  totalClients,
  allUnits,
  platformName,
  themeClasses,
  isDarkMode,
}: PlatformAlertsProps) {
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithPriority | null>(null);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportEmails, setReportEmails] = useState<string[]>([
    ...REPORT_DEFAULT_RECIPIENTS,
  ]);
  const [emailInput, setEmailInput] = useState("");
  const [emailInputError, setEmailInputError] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSentStatus, setReportSentStatus] = useState<"idle" | "success" | "error">("idle");

  const getPriorityLabel = (priority: CompanyWithPriority["priority"]) => {
    switch (priority) {
      case "critical":
        return "Crítico";
      case "highRisk":
        return "Alto Riesgo";
      case "warning":
        return "Advertencia";
      case "healthy":
        return "Óptimo";
    }
  };

  const getPriorityColor = (priority: CompanyWithPriority["priority"]) => {
    switch (priority) {
      case "critical":
        return "text-red-500";
      case "highRisk":
        return "text-orange-500";
      case "warning":
        return "text-yellow-500";
      case "healthy":
        return "text-green-500";
    }
  };

  const getPriorityDotColor = (priority: CompanyWithPriority["priority"]) => {
    switch (priority) {
      case "critical":
        return "bg-red-500";
      case "highRisk":
        return "bg-orange-500";
      case "warning":
        return "bg-yellow-500";
      case "healthy":
        return "bg-green-500";
    }
  };

  const getServiceColor = (pct: number) => {
    if (pct >= 80) return "text-green-500";
    if (pct >= 60) return "text-yellow-500";
    if (pct >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const handleOpenUnits = (company: CompanyWithPriority) => {
    setSelectedCompany(company);
    setShowUnitsModal(true);
  };

  const handleAddEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setEmailInputError("Correo inválido");
      return;
    }
    if (reportEmails.includes(emailInput)) {
      setEmailInputError("El correo ya está agregado");
      return;
    }
    setReportEmails([...reportEmails, emailInput]);
    setEmailInput("");
    setEmailInputError("");
  };

  const handleRemoveEmail = (email: string) => {
    setReportEmails(reportEmails.filter((e) => e !== email));
  };

  const handleSendReport = async () => {
    if (reportEmails.length === 0) return;
    setSendingReport(true);
    setReportSentStatus("idle");
    try {
      await api.post("/entregaServicio/send-report", {
        emails: reportEmails,
        platform: platformName,
        companies: companies.map((c) => ({
          name: c.name,
          totalUnits: c.totalUnits,
          offlineUnits: c.offlineUnits,
          percentage: c.percentage,
          priority: c.priority,
        })),
      });
      setReportSentStatus("success");
      setTimeout(() => {
        setShowReportModal(false);
        setReportEmails([...REPORT_DEFAULT_RECIPIENTS]);
        setEmailInput("");
        setReportSentStatus("idle");
      }, 2000);
    } catch (error) {
      setReportSentStatus("error");
    } finally {
      setSendingReport(false);
    }
  };

  const companyUnits = selectedCompany
    ? allUnits.filter((u) => u.companyId === selectedCompany.id)
    : [];

  const clientsBelow75 = companies.filter((c) => c.percentage < 75).length;

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl overflow-hidden`}
    >
      {/* Header */}
      <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-alarm-warning-line text-red-500"></i>
          </div>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold text-red-700 dark:text-red-400">
              {clientsBelow75} de {totalClients} clientes en estado crítico
            </p>
            <p className="text-xs text-red-600 dark:text-red-400/70 truncate">
              Alertas críticas de {platformName}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowReportModal(true)}
          className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex-shrink-0"
        >
          <i className="ri-mail-send-line"></i>
          <span className="hidden sm:inline">Enviar Reporte</span>
          <span className="sm:hidden">Reporte</span>
        </button>
      </div>

      {/* Table - Desktop */}
      <div className="hidden md:block overflow-hidden">
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full table-auto">
            <thead className={`sticky top-0 ${themeClasses.cardBg} z-10`}>
              <tr className={`border-b ${themeClasses.cardBorder}`}>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider`}
                >
                  Cliente
                </th>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider w-32`}
                >
                  Prioridad
                </th>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider w-28`}
                >
                  <div>Total</div>
                  <div>Unids</div>
                </th>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider w-28`}
                >
                  <div>Unids</div>
                  <div>Offline</div>
                </th>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider w-32`}
                >
                  % Servicio
                </th>
                <th
                  className={`px-4 lg:px-6 py-3 text-center text-xs font-normal ${themeClasses.textSecondary} tracking-wider w-28`}
                >
                  Acción
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {companies.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className={`px-6 py-8 text-center text-sm ${themeClasses.textSecondary}`}
                  >
                    No hay alertas críticas para {platformName}.
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr
                    key={company.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-line text-indigo-500 text-xs"></i>
                        </div>
                        <span
                          className={`text-sm font-medium ${themeClasses.text} truncate max-w-[200px]`}
                          title={company.name}
                        >
                          {company.name || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap w-32">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${getPriorityDotColor(company.priority)}`}
                        ></div>
                        <span
                          className={`text-xs font-semibold ${getPriorityColor(company.priority)}`}
                        >
                          {getPriorityLabel(company.priority)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center w-28">
                      <span className={`text-sm ${themeClasses.text}`}>
                        {company.totalUnits}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center w-28">
                      <span className="text-sm font-semibold text-red-500">
                        {company.offlineUnits}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap w-32">
                      <div className="flex items-center justify-center gap-2">
                        <span
                          className={`text-sm font-semibold ${getServiceColor(company.percentage)}`}
                        >
                          {company.percentage}%
                        </span>
                        <div
                          className={`w-12 h-1.5 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                        >
                          <div
                            className={`h-full rounded-full ${
                              company.percentage >= 80
                                ? "bg-green-500"
                                : company.percentage >= 60
                                  ? "bg-yellow-500"
                                  : company.percentage >= 40
                                    ? "bg-orange-500"
                                    : "bg-red-500"
                            }`}
                            style={{ width: `${company.percentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-center w-28">
                      <button
                        onClick={() => handleOpenUnits(company)}
                        className="px-2 sm:px-3 py-1 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        Unidades
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden max-h-[420px] overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
        {companies.length === 0 ? (
          <div
            className={`px-4 py-8 text-center text-sm ${themeClasses.textSecondary}`}
          >
            No hay alertas críticas para {platformName}.
          </div>
        ) : (
          companies.map((company) => (
            <div key={company.id} className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-indigo-500 text-xs"></i>
                  </div>
                  <span
                    className={`text-sm font-medium ${themeClasses.text} truncate`}
                  >
                    {company.name || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div
                    className={`w-2 h-2 rounded-full ${getPriorityDotColor(company.priority)}`}
                  ></div>
                  <span
                    className={`text-xs font-semibold ${getPriorityColor(company.priority)}`}
                  >
                    {getPriorityLabel(company.priority)}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-4">
                  <div>
                    <span className={themeClasses.textSecondary}>
                      Total Unids:{" "}
                    </span>
                    <span className={`font-semibold ${themeClasses.text}`}>
                      {company.totalUnits}
                    </span>
                  </div>
                  <div>
                    <span className={themeClasses.textSecondary}>
                      Offline:{" "}
                    </span>
                    <span className="font-semibold text-red-500">
                      {company.offlineUnits}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`font-semibold ${getServiceColor(company.percentage)}`}
                    >
                      {company.percentage}%
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenUnits(company)}
                  className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Unidades
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal: Enviar Reporte */}
      {showReportModal && (
        <Modal
          title="Enviar Reporte"
          themeClasses={themeClasses}
          onClose={() => {
            setShowReportModal(false);
            setReportSentStatus("idle");
            setReportEmails([...REPORT_DEFAULT_RECIPIENTS]);
            setEmailInput("");
            setEmailInputError("");
          }}
          width="w-full max-w-lg"
          content={
            <div className="flex flex-col gap-4">
              {reportSentStatus === "success" ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-500 text-2xl"></i>
                  </div>
                  <p className={`text-base font-semibold ${themeClasses.text}`}>
                    Reporte enviado correctamente
                  </p>
                  <p
                    className={`text-sm text-center ${themeClasses.textSecondary}`}
                  >
                    El reporte fue enviado a todos los destinatarios.
                  </p>
                </div>
              ) : (
                <>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    Agrega los correos a los que deseas enviar el reporte de
                    métricas del sistema.
                  </p>

                  {/* Input + botón agregar */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          setEmailInputError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddEmail();
                          }
                        }}
                        placeholder="ejemplo@correo.com"
                        className={`w-full px-3 py-2 text-sm rounded-lg border ${
                          emailInputError
                            ? "border-red-500 focus:ring-red-500/50"
                            : themeClasses.cardBorder
                        } ${themeClasses.cardBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-red-500/50`}
                      />
                      {emailInputError && (
                        <p className="mt-1 text-xs text-red-500">
                          {emailInputError}
                        </p>
                      )}
                    </div>
                    <button
                      aria-label="Agregar"
                      onClick={handleAddEmail}
                      className="px-3 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors flex-shrink-0"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>

                  {/* Lista de correos */}
                  {reportEmails.length > 0 && (
                    <div
                      className={`rounded-lg border ${themeClasses.cardBorder} divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-100"}`}
                    >
                      {reportEmails.map((email) => (
                        <div
                          key={email}
                          className="flex items-center justify-between px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <i
                              className={`ri-mail-line text-sm ${themeClasses.textSecondary}`}
                            ></i>
                            <span
                              className={`text-sm truncate ${themeClasses.text}`}
                            >
                              {email}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveEmail(email)}
                            className={`ml-2 flex-shrink-0 text-sm ${themeClasses.textSecondary} hover:text-red-500 transition-colors`}
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {reportSentStatus === "error" && (
                    <p className="text-sm text-red-500 text-center">
                      Error al enviar el reporte. Inténtalo de nuevo.
                    </p>
                  )}

                  {/* Botón enviar */}
                  <button
                    onClick={handleSendReport}
                    disabled={reportEmails.length === 0 || sendingReport}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingReport ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-send-line"></i>
                        Enviar Reporte ({reportEmails.length}{" "}
                        {reportEmails.length === 1
                          ? "destinatario"
                          : "destinatarios"}
                        )
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          }
        />
      )}

      {/* Modal de unidades por compañía */}
      {showUnitsModal && selectedCompany && (
        <Modal
          title={`${selectedCompany.name} — Unidades (${companyUnits.length})`}
          themeClasses={themeClasses}
          onClose={() => {
            setShowUnitsModal(false);
            setSelectedCompany(null);
          }}
          width="w-full max-w-3xl"
          content={
            <div className="flex flex-col gap-3">
              {companyUnits.length === 0 ? (
                <p
                  className={`text-sm text-center py-8 ${themeClasses.textSecondary}`}
                >
                  No hay unidades para esta compañía.
                </p>
              ) : (
                <div
                  className={`rounded-lg border ${themeClasses.cardBorder} overflow-hidden`}
                >
                  <div
                    className={`grid grid-cols-2 gap-4 px-4 py-3 text-xs font-medium ${themeClasses.textSecondary} ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border-b ${themeClasses.cardBorder}`}
                  >
                    <span>IMEI</span>
                    <span>Compañía</span>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[50vh] overflow-y-auto">
                    {companyUnits.map((unit) => (
                      <div
                        key={unit.id}
                        className={`grid grid-cols-2 gap-4 px-4 py-3 text-sm items-center transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                            <i className="ri-cpu-line text-red-500 text-xs"></i>
                          </div>
                          <span
                            className={`font-medium ${themeClasses.text} truncate font-mono text-xs`}
                          >
                            {unit.name || "—"}
                          </span>
                        </div>
                        <span
                          className={`${themeClasses.textSecondary} truncate`}
                        >
                          {unit.companyName || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}
